import { vec3 } from 'gl-matrix';
import vtkMatrixBuilder from '@kitware/vtk.js/Common/Core/MatrixBuilder';
import vtkCylinderSource from '@kitware/vtk.js/Filters/Sources/CylinderSource';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import type { Vector3 } from '@kitware/vtk.js/types';

import type { DentalArch } from '@/src/io/state-file/schema';
import type { ArcFrame, ArcLengthLUT } from '@/src/utils/dentalCurve';
import { sampleFrameAtArc } from '@/src/utils/dentalCurve';

export interface ImplantCatalogEntry {
  id: string;
  name: string;
  diameterMm: number;
  lengthMm: number;
  manufacturer?: string;
  /** Bundled STL under `src/assets/implants/models/`. */
  stlModel: string;
}

export interface PlacedImplant {
  id: string;
  catalogId: string;
  origin: Vector3;
  axis: Vector3;
  arch: DentalArch;
}

export interface PanoramicImageMeta {
  totalArcMm: number;
  verticalSpacingMm: number;
  /** Pixel rows in the panoramic image. */
  rows: number;
}

export interface ImplantPose {
  origin: Vector3;
  axis: Vector3;
}

export interface PanoramicViewportSize {
  width: number;
  height: number;
}

const EPS = 1e-6;
export const PANORAMIC_CAMERA_MARGIN = 1.05;

export function normalize(v: Vector3): Vector3 {
  const len = Math.hypot(v[0], v[1], v[2]);
  if (len < EPS) return [0, 0, 1];
  return [v[0] / len, v[1] / len, v[2] / len];
}

/** Vertical extent in mm (bottom of pano = 0, top = total height). */
export function panoVerticalExtentMm(meta: PanoramicImageMeta): number {
  return Math.max(0, (meta.rows - 1) * meta.verticalSpacingMm);
}

/**
 * World-space bounds visible through the panoramic parallel camera.
 * Must stay identical to CurvedMPRPanoramicView.fitCamera().
 */
export function panoramicVisibleWorldBounds(
  meta: PanoramicImageMeta,
  viewport: PanoramicViewportSize
) {
  const imageWidth = Math.max(EPS, meta.totalArcMm);
  const imageHeight = Math.max(EPS, panoVerticalExtentMm(meta));
  const aspect = Math.max(EPS, viewport.width / Math.max(EPS, viewport.height));
  const halfVisibleHeight = Math.max(
    10,
    Math.max(imageHeight / 2, imageWidth / (2 * aspect)) *
      PANORAMIC_CAMERA_MARGIN
  );
  const visibleHeight = 2 * halfVisibleHeight;
  const visibleWidth = visibleHeight * aspect;
  return {
    imageWidth,
    imageHeight,
    visibleWidth,
    visibleHeight,
    offsetX: (visibleWidth - imageWidth) / 2,
    offsetY: (visibleHeight - imageHeight) / 2,
  };
}

/**
 * Map panoramic (arc mm, vertical offset from image vertical center) to world LPS.
 */
export function panoMmToWorld(
  arcMm: number,
  verticalMm: number,
  frames: ArcFrame[],
  lut: ArcLengthLUT,
  globalUp: Vector3
): ImplantPose {
  const frame = sampleFrameAtArc(frames, lut, arcMm, globalUp);
  const origin: Vector3 = [
    frame.point[0] + frame.up[0] * verticalMm,
    frame.point[1] + frame.up[1] * verticalMm,
    frame.point[2] + frame.up[2] * verticalMm,
  ];
  const axis = normalize([...frame.up] as Vector3);
  return { origin, axis };
}

/** Platform position only — does not change implant axis (preserves user rotation). */
export function panoMmToWorldOrigin(
  arcMm: number,
  verticalMm: number,
  frames: ArcFrame[],
  lut: ArcLengthLUT,
  globalUp: Vector3
): Vector3 {
  const frame = sampleFrameAtArc(frames, lut, arcMm, globalUp);
  return [
    frame.point[0] + frame.up[0] * verticalMm,
    frame.point[1] + frame.up[1] * verticalMm,
    frame.point[2] + frame.up[2] * verticalMm,
  ];
}

/**
 * Inverse of {@link panoMmToWorld}: find closest arc on the sampled frames.
 */
export function worldToPanoMm(
  origin: Vector3,
  frames: ArcFrame[],
  lut: ArcLengthLUT
): { arcMm: number; verticalMm: number } {
  if (frames.length === 0) {
    return { arcMm: 0, verticalMm: 0 };
  }

  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < frames.length; i++) {
    const f = frames[i];
    const dx = origin[0] - f.point[0];
    const dy = origin[1] - f.point[1];
    const dz = origin[2] - f.point[2];
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 < bestDist) {
      bestIdx = i;
      bestDist = d2;
    }
  }

  const f = frames[bestIdx];
  const arcMm = f.arc;
  const verticalMm =
    (origin[0] - f.point[0]) * f.up[0] +
    (origin[1] - f.point[1]) * f.up[1] +
    (origin[2] - f.point[2]) * f.up[2];

  void lut;
  return { arcMm, verticalMm };
}

/** Map pointer position on the panoramic pane to (arcMm, verticalMm). */
export function clientToPanoMm(
  clientX: number,
  clientY: number,
  el: HTMLElement,
  meta: PanoramicImageMeta
): { arcMm: number; verticalMm: number } {
  const rect = el.getBoundingClientRect();
  const px = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
  const pyFromTop = rect.height > 0 ? (clientY - rect.top) / rect.height : 0;
  const bounds = panoramicVisibleWorldBounds(meta, {
    width: rect.width,
    height: rect.height,
  });
  const worldX = px * bounds.visibleWidth - bounds.offsetX;
  const panoY = (1 - pyFromTop) * bounds.visibleHeight - bounds.offsetY;
  const arcMm = Math.max(0, Math.min(meta.totalArcMm, worldX));
  const verticalMm = panoY - bounds.imageHeight / 2;

  return { arcMm, verticalMm };
}

/** Map pano mm to normalized [0,1] display coords in the panoramic pane. */
export function panoMmToDisplayNorm(
  arcMm: number,
  verticalMm: number,
  meta: PanoramicImageMeta,
  viewport?: PanoramicViewportSize
): { x: number; y: number } {
  const totalHeight = panoVerticalExtentMm(meta);
  const panoY = verticalMm + totalHeight / 2;
  if (!viewport) {
    const x = meta.totalArcMm > 0 ? arcMm / meta.totalArcMm : 0;
    const y = totalHeight > 0 ? 1 - panoY / totalHeight : 0.5;
    return {
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
    };
  }

  const bounds = panoramicVisibleWorldBounds(meta, viewport);
  const x = (bounds.offsetX + arcMm) / bounds.visibleWidth;
  const y = 1 - (bounds.offsetY + panoY) / bounds.visibleHeight;
  return {
    x: Math.max(0, Math.min(1, x)),
    y: Math.max(0, Math.min(1, y)),
  };
}

/**
 * Build a cylinder polydata: platform at `origin`, apical along `axis` for `lengthMm`.
 */
export function buildImplantCylinder(
  diameterMm: number,
  lengthMm: number,
  origin: Vector3,
  axis: Vector3
): vtkPolyData {
  const axisN = normalize(axis);
  const radius = Math.max(0.1, diameterMm / 2);
  const height = Math.max(0.5, lengthMm);

  const source = vtkCylinderSource.newInstance({
    radius,
    height,
    resolution: 24,
    capping: true,
  });

  const yAxis = vec3.fromValues(0, 1, 0);
  const target = vec3.fromValues(axisN[0], axisN[1], axisN[2]);
  const rotAxis = vec3.create();
  vec3.cross(rotAxis, yAxis, target);
  const sinAngle = vec3.length(rotAxis);
  const cosAngle = vec3.dot(yAxis, target);

  let builder = vtkMatrixBuilder
    .buildFromDegree()
    .translate(origin[0], origin[1], origin[2]);

  if (sinAngle > EPS) {
    const angleDeg = (Math.atan2(sinAngle, cosAngle) * 180) / Math.PI;
    vec3.normalize(rotAxis, rotAxis);
    builder = builder.rotate(angleDeg, [
      rotAxis[0],
      rotAxis[1],
      rotAxis[2],
    ] as Vector3);
  } else if (cosAngle < 0) {
    builder = builder.rotate(180, [1, 0, 0]);
  }

  // Default cylinder is centered on origin along Y; shift so platform sits at origin.
  builder = builder.translate(0, height / 2, 0);
  const mat = builder.getMatrix();

  source.update();
  const poly = vtkPolyData.newInstance();
  poly.shallowCopy(source.getOutputData());
  const pts = poly.getPoints();
  if (!pts) return poly;
  const data = pts.getData();
  for (let i = 0; i < data.length; i += 3) {
    const x = data[i];
    const y = data[i + 1];
    const z = data[i + 2];
    data[i] = mat[0] * x + mat[4] * y + mat[8] * z + mat[12];
    data[i + 1] = mat[1] * x + mat[5] * y + mat[9] * z + mat[13];
    data[i + 2] = mat[2] * x + mat[6] * y + mat[10] * z + mat[14];
  }
  pts.modified();
  return poly;
}

/** Distance from implant platform arc to a cross-section tile arc (mm). */
export function implantArcDistanceMm(
  implantOrigin: Vector3,
  frames: ArcFrame[],
  lut: ArcLengthLUT,
  tileArcMm: number
): number {
  const { arcMm } = worldToPanoMm(implantOrigin, frames, lut);
  return Math.abs(arcMm - tileArcMm);
}

function dot3(a: Vector3, b: Vector3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

/**
 * Intersect a finite implant cylinder (platform at `origin`, apical along `axis`)
 * with a plane. Returns the in-plane section center when the plane cuts the body.
 */
export function implantPlaneSection(
  origin: Vector3,
  axis: Vector3,
  lengthMm: number,
  diameterMm: number,
  planeOrigin: Vector3,
  planeNormal: Vector3
): {
  center: Vector3;
  axisInPlane: Vector3;
  radiusMm: number;
} | null {
  const n = normalize(planeNormal);
  const a = normalize(axis);
  const radiusMm = diameterMm / 2;
  const len = Math.max(0.5, lengthMm);

  const rel0: Vector3 = [
    origin[0] - planeOrigin[0],
    origin[1] - planeOrigin[1],
    origin[2] - planeOrigin[2],
  ];
  const f0 = dot3(rel0, n);
  const da = dot3(a, n);
  const f1 = f0 + da * len;

  // Closest approach of the plane to the implant axis segment [platform, apical].
  let tBest = 0;
  let minAbs = Math.abs(f0);

  if (Math.abs(da) < EPS) {
    if (f0 * f1 > 0 && minAbs > radiusMm) {
      return null;
    }
    tBest = Math.abs(f1) < minAbs ? len : 0;
    minAbs = Math.min(Math.abs(f0), Math.abs(f1));
  } else {
    const tHit = -f0 / da;
    const tClamp = Math.max(0, Math.min(len, tHit));
    minAbs = Math.abs(f0 + tClamp * da);
    tBest = tClamp;
    if (Math.abs(f0) < minAbs) {
      minAbs = Math.abs(f0);
      tBest = 0;
    }
    if (Math.abs(f1) < minAbs) {
      minAbs = Math.abs(f1);
      tBest = len;
    }
  }

  if (minAbs > radiusMm) return null;

  const pointOnAxis: Vector3 = [
    origin[0] + a[0] * tBest,
    origin[1] + a[1] * tBest,
    origin[2] + a[2] * tBest,
  ];
  const dist = dot3(
    [
      pointOnAxis[0] - planeOrigin[0],
      pointOnAxis[1] - planeOrigin[1],
      pointOnAxis[2] - planeOrigin[2],
    ],
    n
  );
  const center: Vector3 = [
    pointOnAxis[0] - n[0] * dist,
    pointOnAxis[1] - n[1] * dist,
    pointOnAxis[2] - n[2] * dist,
  ];

  const axisInPlane: Vector3 = [
    a[0] - n[0] * da,
    a[1] - n[1] * da,
    a[2] - n[2] * da,
  ];
  const len2d = Math.hypot(axisInPlane[0], axisInPlane[1], axisInPlane[2]);
  if (len2d < EPS) {
    return { center, axisInPlane: [1, 0, 0], radiusMm };
  }
  return {
    center,
    axisInPlane: normalize(axisInPlane),
    radiusMm,
  };
}
